// src/lib/basemaps.ts — FUNDALURI CONFIGURABILE per hartă (decizia 1.3, PIN-001/BD-035). PUR,
// partajat server↔client: validarea FAIL-CLOSED la scriere (`attributes.basemaps` prin PATCH-ul de
// hartă), parsarea lenientă la citire și șablonul de tile-uri MapLibre (`basemap_tile_url`) — ca
// viewerul intern și `/embed` să construiască sursa raster IDENTIC.
//
// Reguli (de ce): URL-urile ajung în browser (inclusiv în viewerul PUBLIC) → doar http(s), fără
// credențiale în autoritate și fără chei de acces în query (`key=`, `token=`, `apikey=`…): o cheie
// plătită a primăriei ar deveni publică la prima vizualizare. Fundalurile care CER cheie se pun prin
// proxy propriu sau nu se pun (decizie de produs, documentată în contract).

export type BasemapType = "xyz" | "wms"

export interface Basemap {
	name: string
	type: BasemapType
	url: string // xyz: șablon cu {z}/{x}/{y} (sau {quadkey}); wms: URL-ul de bază GetMap (fără query)
	attribution: string // "" = fără atribuire afișată (tipărirea o preia și ea)
	layers: string // wms: LAYERS=; gol pe xyz
	format: string // wms: FORMAT= (implicit image/png); gol pe xyz
	tile_size: number // 256 | 512 (xyz); wms cere 256
	max_zoom: number // 1..22 (implicit 19)
}

export const MAX_BASEMAPS = 10
const MAX_NAME = 60
const MAX_URL = 500
const MAX_ATTRIBUTION = 200

/** Parametri de query care poartă chei/semnături — respinși fail-closed (URL-ul devine public). */
const SECRET_PARAM_RE =
	/^(api[_-]?key|key|token|access[_-]?token|apikey|signature|sig|auth|password|pass|secret|client[_-]?secret)$/i

import { validation_error as err, type Validation } from "./validation"

function is_obj(x: unknown): x is Record<string, unknown> {
	return typeof x === "object" && x !== null && !Array.isArray(x)
}

/** Motivul pentru care un URL de fundal NU e acceptat, sau "" dacă e în regulă. */
export function basemap_url_problem(url: string, type: BasemapType): string {
	if (!url || url.length > MAX_URL) {
		return "URL-ul lipsește sau depășește 500 de caractere"
	}
	// Șabloanele cu {z}/{x}/{y} nu trec prin `new URL` nealterate → validăm pe o copie „neutră".
	const probe = url.replace(/\{[^}]*\}/g, "0")
	let u: URL
	try {
		u = new URL(probe)
	} catch {
		return "URL-ul nu e valid"
	}
	if (u.protocol !== "http:" && u.protocol !== "https:") {
		return "doar adrese http(s)"
	}
	if (u.username || u.password) {
		return "URL-ul conține credențiale (utilizator:parolă) — nu se pot pune într-un fundal public"
	}
	for (const k of u.searchParams.keys()) {
		if (SECRET_PARAM_RE.test(k)) {
			return `URL-ul conține o cheie de acces ('${k}') — cheile nu se pun în fundaluri (ajung în browser, inclusiv în harta publică)`
		}
	}
	if (type === "xyz") {
		const has_xyz = /\{z\}/i.test(url) && /\{x\}/i.test(url) && /\{-?y\}/i.test(url)
		if (!has_xyz && !/\{quadkey\}/i.test(url)) {
			return "șablonul XYZ trebuie să conțină {z}, {x} și {y} (sau {quadkey})"
		}
	} else if (u.search) {
		return "pentru WMS dă doar URL-ul de bază (fără parametri) — LAYERS/FORMAT se setează separat"
	}
	return ""
}

/** Validează UN fundal (formă liberă din JSON) → forma normalizată. */
export function validate_basemap(v: unknown): Validation<Basemap> {
	if (!is_obj(v)) {
		return err("fundalul trebuie să fie un obiect {name, type, url, …}")
	}
	const name = typeof v.name === "string" ? v.name.trim() : ""
	if (!name || name.length > MAX_NAME) {
		return err("numele lipsește sau depășește 60 de caractere")
	}
	const type = v.type === "xyz" || v.type === "wms" ? v.type : null
	if (!type) {
		return err(`tip necunoscut '${String(v.type)}' (permise: xyz, wms)`)
	}
	const url = typeof v.url === "string" ? v.url.trim() : ""
	const problem = basemap_url_problem(url, type)
	if (problem) {
		return err(`'${name}': ${problem}`)
	}
	const attribution = typeof v.attribution === "string" ? v.attribution.trim() : ""
	if (attribution.length > MAX_ATTRIBUTION) {
		return err(`'${name}': atribuirea depășește 200 de caractere`)
	}
	// AttributionControl-ul MapLibre randează atribuirea ca HTML (innerHTML) → un editor ar putea
	// planta markup (XSS stocat, servit și în viewerul PUBLIC). Fail-closed: DOAR text, fără `<`/`>`;
	// clientul ESCAPEAZĂ oricum la randare (apărare în adâncime, vezi contractul).
	if (/[<>]/.test(attribution)) {
		return err(`'${name}': atribuirea e doar text — fără etichete HTML (<, >)`)
	}
	const layers = typeof v.layers === "string" ? v.layers.trim() : ""
	if (type === "wms" && !layers) {
		return err(`'${name}': WMS cere 'layers' (numele stratului/straturilor din serviciu)`)
	}
	const format =
		typeof v.format === "string" && v.format.trim()
			? v.format.trim()
			: type === "wms"
				? "image/png"
				: ""
	if (format && !/^image\/[a-z0-9.+-]+$/i.test(format)) {
		return err(`'${name}': format invalid '${format}' (ex. image/png)`)
	}
	const tile_size = v.tile_size === 512 ? 512 : 256
	const mz = typeof v.max_zoom === "number" ? Math.round(v.max_zoom) : 19
	if (!Number.isFinite(mz) || mz < 1 || mz > 22) {
		return err(`'${name}': max_zoom trebuie să fie între 1 și 22`)
	}
	return {
		ok: true,
		value: {
			name,
			type,
			url,
			attribution,
			layers: type === "wms" ? layers : "",
			format,
			tile_size: type === "wms" ? 256 : tile_size,
			max_zoom: mz,
		},
	}
}

/** Lista de fundaluri a hărții — validare completă, fail-closed. `null`/absent = listă goală. */
export function validate_basemaps(v: unknown): Validation<Basemap[]> {
	if (v === null || v === undefined) {
		return { ok: true, value: [] }
	}
	if (!Array.isArray(v)) {
		return err("basemaps trebuie să fie o listă de fundaluri")
	}
	if (v.length > MAX_BASEMAPS) {
		return err(`cel mult ${MAX_BASEMAPS} fundaluri per hartă`)
	}
	const out: Basemap[] = []
	const names = new Set<string>()
	for (const [i, item] of v.entries()) {
		const one = validate_basemap(item)
		if (!one.ok) {
			return err(`fundalul #${i + 1}: ${one.error}`)
		}
		if (names.has(one.value.name.toLowerCase())) {
			return err(`fundalul #${i + 1}: numele '${one.value.name}' se repetă`)
		}
		names.add(one.value.name.toLowerCase())
		out.push(one.value)
	}
	return { ok: true, value: out }
}

/** Citire LENIENTĂ (EAV liber, posibil string JSON): intrările invalide se ignoră tăcut. */
export function parse_basemaps(v: unknown): Basemap[] {
	let raw = v
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw)
		} catch {
			return []
		}
	}
	if (!Array.isArray(raw)) {
		return []
	}
	const out: Basemap[] = []
	for (const item of raw.slice(0, MAX_BASEMAPS)) {
		const one = validate_basemap(item)
		if (one.ok && !out.some((b) => b.name.toLowerCase() === one.value.name.toLowerCase())) {
			out.push(one.value)
		}
	}
	return out
}

/**
 * Șablonul de tile-uri pentru o sursă `raster` MapLibre: xyz → URL-ul ca atare; wms → GetMap 1.1.1 în
 * EPSG:3857 cu `{bbox-epsg-3857}` (placeholder-ul nativ MapLibre), 256×256, transparent.
 */
export function basemap_tile_url(b: Basemap): string {
	if (b.type === "xyz") {
		return b.url
	}
	const q = new URLSearchParams({
		SERVICE: "WMS",
		REQUEST: "GetMap",
		VERSION: "1.1.1",
		LAYERS: b.layers,
		STYLES: "",
		FORMAT: b.format || "image/png",
		TRANSPARENT: "true",
		SRS: "EPSG:3857",
		WIDTH: "256",
		HEIGHT: "256",
	})
	// `{bbox-epsg-3857}` NU trece prin URLSearchParams (ar fi percent-encodat) — se lipește literal.
	return `${b.url}${b.url.includes("?") ? "&" : "?"}${q.toString()}&BBOX={bbox-epsg-3857}`
}
